/**
 * rc-a11y.js (RC-owned): accessibility fixes over production's carried sections and header.
 * Loaded on every page, deferred (layout/theme.liquid). Production's code (rc-legacy.js) stays verbatim.
 *
 * 1. D-068, QA DEF-035: under prefers-reduced-motion: reduce, autoplaying videos are paused, and stay paused until
 *    the shopper starts one. Where production renders its pause button (snippets/video-pause-button.liquid), the
 *    button is set to its paused state ("Play video"). Other videos get the native controls, except decorative
 *    (aria-hidden) ones, videos inside a link or a button, Replo builder markup, and the PDP video carousel's
 *    previews, which open their own player. Left alone: Horizon's video-background-component (its CSS hides the
 *    video under reduced motion), the split banners' background video (its own script skips autoplay), and Horizon's
 *    deferred-media, which keeps its own play state (its autoplay setting stays Horizon's).
 * 2. D-069, QA DEF-040: the mobile menu (snippets/nav-contents.liquid) moves focus into its panel when it opens,
 *    keeps Tab within the trigger and the panel, closes on Escape through production's own toggle, and returns
 *    focus to the trigger when it closes with focus inside. It also closes when the header search trigger
 *    (.rc-search-trigger) opens the search modal, so closing the modal does not land back in the menu.
 */
(function () {
  'use strict';

  // ---- 1. Reduced motion -------------------------------------------------------------------------------------
  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var HANDLES_ITSELF = 'video-background-component, deferred-media, .sb__bg-video';
  var NO_NATIVE_CONTROLS = 'a, button, [data-rid], .alchemy__element, .video-carousel';
  var GESTURE_WINDOW_MS = 1000;
  // What a shopper activates to start a video: a control, the video itself, its label, or production's div launchers
  // (the PDP carousel's and Watch The Reviews' [data-full] tiles, and that modal's next arrow). Not the page itself:
  // a tap before a scroll is not a request for playback. Space activates only buttons, videos and summaries; on a
  // link or the page it scrolls.
  var ACTIVATABLE =
    'a[href], button, [role="button"], video, label, summary, [data-full], .watch-the-reviews__arrow-button';
  var SPACE_ACTIVATES = 'button, [role="button"], video, summary';
  var lastGesture = 0;
  var startedByShopper = typeof WeakSet === 'function' ? new WeakSet() : null;

  function shopperJustActed() {
    return Date.now() - lastGesture < GESTURE_WINDOW_MS;
  }

  function wantsNativeControls(video) {
    if (video.closest(NO_NATIVE_CONTROLS)) return false;
    // aria-hidden marks production's decorative backgrounds (feature rows, split banners); a slick carousel's
    // inactive slide is only hidden until it is shown.
    var hidden = video.closest('[aria-hidden="true"]');
    return !hidden || hidden.classList.contains('slick-slide');
  }

  function markPaused(video) {
    var wrapper = video.closest('.video-wrapper--autoplay');
    var button = wrapper && wrapper.querySelector('.video-pause-btn');
    if (button) {
      // The paused state of production's toggle (rc-legacy.js, "Autoplay video pause/play toggle").
      button.setAttribute('aria-label', 'Play video');
      button.setAttribute('data-playing', 'false');
      var icon = button.querySelector('.video-pause-btn__icon');
      if (icon) icon.textContent = '▶';
    } else if (wantsNativeControls(video)) {
      video.controls = true;
    }
  }

  function stillVideo(video) {
    if (video.closest(HANDLES_ITSELF)) return;
    if (!video.hasAttribute('autoplay') && video.paused) return;
    video.removeAttribute('autoplay');
    if (!video.paused) video.pause();
    markPaused(video);
  }

  function activates(event, selector) {
    var target = event.target instanceof Element ? event.target : null;
    return !!(event.isTrusted && target && target.closest(selector));
  }

  if (reducedMotion && startedByShopper) {
    // A click or Enter on something activatable, or Space on a button, is the shopper asking for playback.
    // Scrolling, hovering and keys on the page itself are not.
    document.addEventListener('click', function (event) {
      if (activates(event, ACTIVATABLE)) lastGesture = Date.now();
    }, true);
    document.addEventListener('keydown', function (event) {
      var starts = event.key === 'Enter' ? activates(event, ACTIVATABLE) : event.key === ' ' && activates(event, SPACE_ACTIVATES);
      if (starts) lastGesture = Date.now();
    }, true);

    // Production's scripts restart videos (in-view players, hover previews). Media events don't bubble: capture.
    document.addEventListener('play', function (event) {
      var video = event.target;
      if (!reducedMotion.matches || !(video instanceof HTMLVideoElement) || video.closest(HANDLES_ITSELF)) return;
      if (startedByShopper.has(video)) return;
      if (shopperJustActed()) {
        startedByShopper.add(video);
        return;
      }
      video.pause();
      markPaused(video);
    }, true);

    var videoObserver = new MutationObserver(function (records) {
      if (!reducedMotion.matches) return;
      var acted = shopperJustActed();
      records.forEach(function (record) {
        var videos = [];
        if (record.type === 'attributes') {
          videos.push(record.target);
        } else {
          record.addedNodes.forEach(function (node) {
            if (node instanceof HTMLVideoElement) videos.push(node);
            else if (node instanceof Element) videos.push.apply(videos, node.querySelectorAll('video'));
          });
        }
        videos.forEach(function (video) {
          if (!(video instanceof HTMLVideoElement) || startedByShopper.has(video)) return;
          // A player the shopper just opened (the PDP carousel's popup) carries its own controls.
          if (acted && video.controls) startedByShopper.add(video);
          else stillVideo(video);
        });
      });
    });

    var startReducedMotion = function () {
      if (!reducedMotion.matches) return;
      document.querySelectorAll('video').forEach(stillVideo);
      videoObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['autoplay'],
      });
    };
    startReducedMotion();
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', function () {
        if (reducedMotion.matches) startReducedMotion();
        else videoObserver.disconnect();
      });
    }
  }

  // ---- 2. Mobile menu focus ----------------------------------------------------------------------------------
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
    'textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';
  var TEXT_ENTRY = 'input, select, textarea';
  var trigger = document.querySelector('.nav--mobile .menuToggle');
  var panel = document.getElementById('ariaHamburgerID');

  function isShown(element) {
    return element.getClientRects().length > 0 && window.getComputedStyle(element).visibility !== 'hidden';
  }

  function menuItems() {
    return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (element) {
      return element !== trigger && isShown(element);
    });
  }

  // Another overlay owns the keyboard: a modal dialog is open, or focus sits in the legacy cart drawer or an
  // aria-modal widget (the Accessibly app's panel).
  function otherOverlayActive(target) {
    try {
      if (document.querySelector('dialog:modal')) return true;
    } catch (error) {
      // :modal unsupported: fall through to the focus check.
    }
    if (!(target instanceof Element) || target === trigger || panel.contains(target)) return false;
    return !!target.closest('dialog, [aria-modal="true"], cart-drawer');
  }

  if (trigger && panel) {
    var closedLabel = trigger.getAttribute('aria-label');
    var menuOpen = trigger.getAttribute('aria-expanded') === 'true';

    // Production's toggle (rc-legacy.js openHeaderMenu / closeHeaderMenu) flips aria-expanded on the trigger.
    new MutationObserver(function () {
      var open = trigger.getAttribute('aria-expanded') === 'true';
      if (open === menuOpen) return;
      menuOpen = open;
      if (open) {
        trigger.setAttribute('aria-label', 'Close navigation menu');
        if (!isShown(trigger)) return; // desktop: the mobile trigger isn't rendered
        requestAnimationFrame(function () {
          if (!menuOpen) return;
          var items = menuItems();
          // Not a text field: focusing one would raise the phone's keyboard.
          var first = items.filter(function (item) { return !item.matches(TEXT_ENTRY); })[0] || items[0];
          if (first) first.focus({ preventScroll: true });
        });
      } else {
        if (closedLabel === null) trigger.removeAttribute('aria-label');
        else trigger.setAttribute('aria-label', closedLabel);
        if (panel.contains(document.activeElement) && isShown(trigger)) trigger.focus({ preventScroll: true });
      }
    }).observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });

    // Capture phase, and the keys the open menu handles stop here: the Accessibly app's own document listener
    // focuses its "Skip to menu" link on a page's first Tab (QA saw it as the first Tab leaving the menu).
    document.addEventListener('keydown', function (event) {
      if (!menuOpen || event.defaultPrevented || !isShown(trigger) || otherOverlayActive(event.target)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        trigger.click(); // production's toggle reads aria-expanded="true" on the clicked element and closes
        trigger.focus();
        return;
      }

      if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
      var items = [trigger].concat(menuItems());
      if (items.length < 2) return;
      var index = items.indexOf(document.activeElement);
      var step = event.shiftKey ? -1 : 1;
      var next =
        index === -1 ? items[event.shiftKey ? items.length - 1 : 1] : items[(index + step + items.length) % items.length];
      event.preventDefault();
      event.stopPropagation();
      next.focus();
    }, true);

    // The search trigger sits beside the open menu's toggle; Horizon's on:click opens the modal on its own.
    document.addEventListener('click', function (event) {
      if (!menuOpen || !(event.target instanceof Element) || !event.target.closest('.rc-search-trigger')) return;
      trigger.click();
    }, true);
  }
})();
